import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild, signal, computed, effect, Injector, afterNextRender } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import { ToastService } from 'src/app/@core/services/toast.service';
import { CommandComponent, DateGroup, type CommandItem } from 'src/app/@core/components/ui/command/command.component';
import { DeleteConfirmationComponent } from 'src/app/@core/components/ui/delete-confirmation/delete-confirmation.component';
import { threadKeys } from 'src/app/angular-query/keys/thread-keys';
import { createInfiniteQueryHook } from 'src/app/angular-query/use-query';
import { SidebarService } from 'src/app/app-layout/services/sidebar.service';
import { SidebarViewService } from 'src/app/app-layout/services/sidebar-view.service';
import { ISideBarMenu } from '../../../../app-layout/models/sidebar.model';
import { GENERAL_AGENT_SVG, SVG } from 'src/app/common/svgs';
import { SIDEBAR_IDS } from 'src/app/common/utils';
import { IconSizeEnum, IconSource, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { SocketService, SOCKET_EVENTS } from 'src/app/@core/services/socket.service';
import { EditThreadComponent } from '../../components/dialogs/edit-thread/edit-thread.component';
import { ManageMemoriesComponent } from '../../components/dialogs/manage-memories/manage-memories.component';
import { ManageSkillsComponent } from '../../components/dialogs/manage-skills/manage-skills.component';
import { THREAD_METADATA_STATUS } from '../../models/model';
import { FeatureFlagService } from 'src/app/@core/services/feature-flag.service';
import { AGENT_FEATURES } from 'src/app/pricing/models';
import { GeneralAgentService } from '../../services/general-agent.service';
import { ThreadMessagesService } from '../../services/thread-messages.service';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { UserService } from 'src/app/contract-management/services/user.service';
import { PromptLibraryWalkthroughComponent } from '../../components/dialogs/prompt-library-walkthrough/prompt-library-walkthrough.component';
import moment from 'moment';

const PROMPT_LIBRARY_WALKTHROUGH_KEY = 'PROMPT_LIBRARY';
/**
 * AgentPageComponent
 * 
 * Main container for the "Ask Bob v2.0" (General Agent) chat interface.
 * Handles the overall layout including:
 * - Sidebar with thread list, search, and navigation
 * - Main chat area (routed content)
 * - Thread management (create, edit, delete)
 * - Memory management dialog
 * 
 * This component is responsible for:
 * 1. Loading and displaying the thread list in the sidebar
 * 2. Handling thread navigation and selection
 * 3. Thread CRUD operations (create, update, delete)
 * 4. Search functionality with infinite scroll
 * 5. Managing dialog states (edit, delete, search, memories)
 */
@Component({
    selector: 'app-agent-page',
    standalone: true,
    host: { class: 'flex flex-col flex-1 min-h-0 h-full overflow-hidden' },
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        DeleteConfirmationComponent,
        DialogComponent,
        CommandComponent,
        TooltipModule,
        EditThreadComponent,
        ManageMemoriesComponent,
        ManageSkillsComponent,
        PromptLibraryWalkthroughComponent
    ],
    templateUrl: './agent-page.component.html',
    styleUrl: './agent-page.component.scss'
})
export class AgentPageComponent implements OnInit, AfterViewInit, OnDestroy {
    // ============================================
    // Constants & Static Data
    // ============================================
    readonly threadSidebarId = SIDEBAR_IDS.GENERAL_AGENT;
    readonly THREADS_PAGE_SIZE = 30;
    readonly SVG = SVG;
    readonly GENERAL_AGENT_SVG = GENERAL_AGENT_SVG;
    readonly THREAD_METADATA_STATUS = THREAD_METADATA_STATUS;

    // ============================================
    // State - Sidebar & Threads
    // ============================================
    sidebarMenu: ISideBarMenu[] = [];

    // Computed signal: Derives threads from query data
    threads = computed(() => {
        const data = this.threadsQuery.data();
        if (data?.pages) {
            return data.pages.flatMap((page: any) => {
                if (page?.success && page.data) {
                    return page.data.threads;
                }
                return [];
            });
        }
        return [];
    });

    // ============================================
    // State - Queries (Tanstack Query)
    // ============================================
    threadsQuery = createInfiniteQueryHook<{
        threads: Array<{
            title: string;
            _id: string;
            isOwner: boolean;
            threadMetadataId: string;
            agentStatus?: string;
            createdAt: string;
        }>;
        total: number;
    }>(
        threadKeys.all,
        ({ pageParam }) => this.generalAgentService.getThreads(pageParam, this.THREADS_PAGE_SIZE),
        { pageSize: this.THREADS_PAGE_SIZE }
    );

    searchThreadsQuery = createInfiniteQueryHook(
        () => [...threadKeys.search, this.searchValue.trim()],
        ({ pageParam }) =>
            this.generalAgentService.getThreads(
                pageParam,
                this.THREADS_PAGE_SIZE,
                { searchValue: this.searchValue.trim() }
            ),
        { pageSize: this.THREADS_PAGE_SIZE, enabled: false }
    );

    // ============================================
    // State - Delete Thread Dialog
    // ============================================
    deleteThreadDialogVisible = false;
    threadIdToDelete: string = '';

    // ============================================
    // State - Edit Thread Dialog
    // ============================================
    editThreadDialogVisible = signal<boolean>(false);
    threadIdToEdit: string = '';
    threadTitleToEdit = signal<string>('');

    // ============================================
    // State - Search Dialog
    // ============================================
    searchDialogVisible = false;

    // Computed signal: Derives search results from query data
    searchResults = computed(() => {
        const data = this.searchThreadsQuery.data();
        if (data?.pages) {
            return data.pages.flatMap((page: any) => {
                if (page?.success && page.data) {
                    return page.data.threads;
                }
                return [];
            });
        }
        return [];
    });

    searchValue = '';
    searchCommandItems: CommandItem[] = [];
    searchLoading = signal<boolean>(false);
    private searchScrollListener?: EventListener;
    private searchDebounceTimer?: ReturnType<typeof setTimeout>;

    // ============================================
    // State - Manage Memories Dialog
    // ============================================
    showManageMemoriesDialog = signal<boolean>(false);

    // ============================================
    // State - Manage Skills Dialog
    // ============================================
    skillsDialogVisible = signal<boolean>(false);

    // ============================================
    // State - Prompt Library Walkthrough Dialog
    // ============================================
    promptLibraryHighlighted = signal<boolean>(false);
    @ViewChild('walkthroughRef') private walkthroughRef!: PromptLibraryWalkthroughComponent;

    // ============================================
    // Effect cleanup
    // ============================================
    private effectRefs: Array<() => void> = [];
    private threadStatusSubscription?: Subscription;

    // ============================================
    // Constructor & Dependency Injection
    // ============================================
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private generalAgentService: GeneralAgentService,
        private sidebarService: SidebarService,
        private sidebarViewService: SidebarViewService,
        private queryClient: QueryClient,
        private toastService: ToastService,
        private injector: Injector,
        private socketService: SocketService,
        private featureFlagService: FeatureFlagService,
        private threadMessagesService: ThreadMessagesService,
        private authService: AuthenticationService,
        private userService: UserService
    ) { }

    // ============================================
    // Lifecycle Hooks
    // ============================================
    ngOnInit(): void {
        this.setupSidebarMenu();
        this.setupThreadStatusListener();
        // Create effects that will be cleaned up on destroy
        const threadsEffect = effect(() => {
            // Track changes to threads and live status signals
            const threads = this.threads();
            this.threadMessagesService.threadMetadataStatus();
            this.threadMessagesService.activeThreadId();
            if (threads.length > 0 || this.threadsQuery.isFetched()) {
                this.setupSidebarMenu();
            }
        }, { injector: this.injector });

        const searchEffect = effect(() => {
            // Track changes to search results computed signal
            this.searchResults();
            if (this.searchDialogVisible) {
                this.updateSearchCommandItems();
            }
        }, { injector: this.injector });

        const walkthroughEffect = effect(() => {
            this.promptLibraryHighlighted();
            this.setupSidebarMenu();
        }, { injector: this.injector });

        // Store cleanup functions
        this.effectRefs.push(() => threadsEffect.destroy());
        this.effectRefs.push(() => searchEffect.destroy());
        this.effectRefs.push(() => walkthroughEffect.destroy());
    }

    ngAfterViewInit(): void {
        afterNextRender(() => this.checkPromptLibraryWalkthrough(), { injector: this.injector });
    }

    ngOnDestroy(): void {
        this.sidebarViewService.popView();
        this.cleanupSearchListeners();
        if (this.threadStatusSubscription) {
            this.threadStatusSubscription.unsubscribe();
        }

        // Clean up all effects
        this.effectRefs.forEach(cleanup => cleanup());
    }

    /**
     * Listen for THREAD_STATUS_CHANGED from user room.
     * Updates the TanStack query cache so the sidebar shows correct status
     * (spinner / completed / error) for all threads, including non-active and queued ones.
     */
    private setupThreadStatusListener(): void {
        this.threadStatusSubscription = this.socketService
            .on<{ threadId: string; status: string; queuePosition?: number | null }>(SOCKET_EVENTS.THREAD_STATUS_CHANGED)
            .subscribe((res) => {
                const data = res[0];
                if (!data?.threadId || !data?.status) return;
                this.queryClient.setQueryData(threadKeys.all, (oldData: any) => {
                    if (!oldData?.pages) return oldData;
                    const updatedPages = oldData.pages.map((page: any) => {
                        if (!page?.data?.threads) return page;
                        return {
                            ...page,
                            data: {
                                ...page.data,
                                threads: page.data.threads.map((t: any) =>
                                    t._id === data.threadId
                                        ? { ...t, threadStatus: data.status, queuePosition: data.queuePosition ?? null }
                                        : t
                                )
                            }
                        };
                    });
                    return { ...oldData, pages: updatedPages };
                });
            });
    }

    // ============================================
    // Sidebar Configuration
    // ============================================
    /**
     * Configures the sidebar menu with:
     * - New chat button
     * - Search chats button
     * - Manage memories button
     * - List of existing threads with options (edit/delete for owned threads)
     */

    getThreadIcon(thread: any): string {
        switch (thread.threadStatus) {
            case THREAD_METADATA_STATUS.PENDING:
                return 'message-circle-dashed';
            case THREAD_METADATA_STATUS.IN_PROGRESS:
                return 'pi pi-spin pi-spinner';
            case THREAD_METADATA_STATUS.INACTIVE:
                return 'message-circle-off';
            case THREAD_METADATA_STATUS.COMPLETED:
                return 'message-circle';
            case THREAD_METADATA_STATUS.ERROR:
                return 'message-circle-x';
            case THREAD_METADATA_STATUS.PAUSED:
                return 'message-circle-warning';
            case THREAD_METADATA_STATUS.CANCELLED:
                return 'message-circle-x';
            case THREAD_METADATA_STATUS.INTERRUPTED:
                return 'message-circle-warning';
            default:
                return 'message-circle';
        }
    }
    setupSidebarMenu(): void {
        const activeThreadId = this.threadMessagesService.activeThreadId();
        const liveStatus = this.threadMessagesService.threadMetadataStatus();
        const chatItems = this.threads().map((thread: any) => {
            const effectiveStatus = (thread._id === activeThreadId && liveStatus)
                ? liveStatus as THREAD_METADATA_STATUS
                : thread.threadStatus;
            const threadWithLiveStatus = { ...thread, threadStatus: effectiveStatus };
            return {
                label: thread.title || `Chat ${thread._id?.substring(0, 8)}...`,
                icon: this.getThreadIcon(threadWithLiveStatus),
                iconSource: (effectiveStatus === THREAD_METADATA_STATUS.IN_PROGRESS) ? IconSourceEnum.PRIMENG : IconSourceEnum.LUCIDE,
                iconSize: IconSizeEnum.SM,
                route: `/bob/thread`,
                queryParams: {
                    threadId: thread._id,
                    threadMetadataId: thread.threadMetadataId
                },
                options: thread.owned ? [
                    {
                        label: 'Edit',
                        icon: 'pencil',
                        iconSource: 'lucide' as IconSource,
                        click: () => this.openEditThreadDialog(thread._id),
                    },
                    {
                        label: 'Delete',
                        icon: 'trash-2',
                        iconSource: 'lucide' as IconSource,
                        click: () => this.openDeleteThreadDialog(thread._id),
                        class: 'text-danger'
                    },
                ] : []
            };
        });

        this.sidebarMenu = [
            {
                header: 'New chat',
                svg: GENERAL_AGENT_SVG.NEW_CHAT,
                route: '/bob',
                exactRoute: true,
                click: () => this.startNewChat()
            },
            {
                header: 'Search chats',
                svg: GENERAL_AGENT_SVG.SEARCH,
                click: () => this.openSearchDialog()
            },
            {
                header: 'Manage Memories',
                svg: GENERAL_AGENT_SVG.LIBRARY,
                click: () => this.openManageMemoriesDialog()
            },
            ...(this.featureFlagService.hasSpecificAgentFeature(AGENT_FEATURES.SKILL_UPLOAD)
                ? [{
                    header: 'Manage Skills',
                    svg: GENERAL_AGENT_SVG.KNOWLEDGE_BASE,
                    click: () => this.openManageSkillsDialog()
                }]
                : []),
            {
                /** Prompt Library — saved and workspace-shared prompts. */
                header: 'Prompt Library',
                svg: GENERAL_AGENT_SVG.PROMPT_LIBRARY,
                route: '/bob/prompt-library',
                exactRoute: true,
                highlighted: this.promptLibraryHighlighted(),
                infoClick: () => {
                    const el = document.getElementById(SIDEBAR_IDS.MAIN);
                    if (el) {
                        this.promptLibraryHighlighted.set(true);
                        this.walkthroughRef.show(el);
                    }
                },
            },
            {
                header: 'Chats',
                items: chatItems,
                collapsed: false,
                isCollapsible: false,
                loading: this.threadsQuery.isFetching()
            }
        ];

        // Push or update the Bob view in the shared sidebar
        this.sidebarViewService.pushView({
            id: 'bob-v25',
            title: 'Ask Bob',
            menu: this.sidebarMenu,
            backRoute: '/explore',
            showFooter: false,
            onMenuScroll: (event: Event) => this.onThreadsScroll(event),
        });
    }

    // ============================================
    // Thread Navigation
    // ============================================
    /**
     * Navigates to a new empty chat
     */
    startNewChat(): void {
        this.router.navigate(['/bob']);
    }

    /**
     * Handles infinite scroll for threads list in sidebar
     */
    onThreadsScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const threshold = 20;
        const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

        if (atBottom && this.threadsQuery.hasNextPage() && !this.threadsQuery.isFetchingNextPage()) {
            this.threadsQuery.fetchNextPage();
        }
    }

    // ============================================
    // Thread CRUD Operations
    // ============================================
    /**
     * Opens the edit thread dialog
     */
    openEditThreadDialog(threadId: string): void {
        this.threadIdToEdit = threadId;
        const thread = this.threads().find(t => t._id === threadId);
        this.threadTitleToEdit.set(thread?.title || '');
        this.editThreadDialogVisible.set(true);
    }

    /**
     * Updates the thread title via API and refreshes local state
     */
    updateThreadTitle(): void {
        const newTitle = this.threadTitleToEdit();

        if (!newTitle.trim()) {
            this.toastService.error('Thread title is required');
            return;
        }

        this.generalAgentService.updateThreadTitle(this.threadIdToEdit, newTitle)
            .then(res => {
                if (res.success) {
                    const updatedThread = res.data;

                    // Update local threads list
                    const index = this.threads().findIndex(t => t._id === updatedThread._id);
                    if (index !== -1) {
                        // Trigger a refetch to update the computed signal
                        this.queryClient.invalidateQueries({ queryKey: threadKeys.all });
                    }

                    // Update cached thread data
                    this.queryClient.setQueryData(
                        threadKeys.getThread(this.threadIdToEdit),
                        (oldData: any) => {
                            if (oldData?.success && oldData.data) {
                                return {
                                    ...oldData,
                                    data: {
                                        ...oldData.data,
                                        thread: {
                                            ...oldData.data.thread,
                                            title: updatedThread.title
                                        }
                                    }
                                };
                            }
                            return oldData;
                        }
                    );

                    this.toastService.success('Thread title updated successfully');
                    this.closeEditThreadDialog();
                }
            })
            .catch(err => {
                console.error('Error updating thread title:', err);
                this.toastService.error('Failed to update thread title');
            });
    }

    /**
     * Closes the edit thread dialog and resets state
     */
    closeEditThreadDialog(): void {
        this.editThreadDialogVisible.set(false);
        this.threadIdToEdit = '';
        this.threadTitleToEdit.set('');
    }

    /**
     * Opens the delete confirmation dialog
     */
    openDeleteThreadDialog(threadId: string): void {
        this.threadIdToDelete = threadId;
        this.deleteThreadDialogVisible = true;
    }

    /**
     * Deletes the thread via API and redirects to new chat
     */
    deleteThread(): void {
        const threadIdToRemove = this.threadIdToDelete;
        this.generalAgentService.deleteThread(threadIdToRemove)
            .then(res => {
                if (res.success) {
                    this.deleteThreadDialogVisible = false;
                    this.threadIdToDelete = '';

                    this.queryClient.setQueryData(threadKeys.all, (oldData: any) => {
                        if (!oldData?.pages) return oldData;
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page: any) => {
                                if (!page?.data?.threads) return page;
                                const hadThread = page.data.threads.some((t: any) => t._id === threadIdToRemove);
                                const threads = page.data.threads.filter((t: any) => t._id !== threadIdToRemove);
                                return {
                                    ...page,
                                    data: {
                                        ...page.data,
                                        threads,
                                        total: hadThread ? Math.max(0, (page.data.total ?? 0) - 1) : page.data.total
                                    }
                                };
                            })
                        };
                    });

                    this.toastService.success('Thread deleted successfully');
                    this.router.navigate(['/bob']).then(() => {
                        this.queryClient.invalidateQueries({ queryKey: threadKeys.all });
                    });
                }
            })
            .catch(err => {
                console.error('Error deleting thread:', err);
                this.toastService.error('Failed to delete thread');
            });
    }

    // ============================================
    // Search Functionality
    // ============================================
    /**
     * Opens the search dialog with all threads initially
     */
    openSearchDialog(): void {
        if (this.threadsQuery.isLoading() && this.threads().length === 0) {
            return;
        }

        this.searchDialogVisible = true;
        this.searchValue = '';
        this.searchLoading.set(false);
        this.updateSearchCommandItems();
        setTimeout(() => this.setupSearchScrollListener(), 0);
    }

    /**
     * Handles search input changes with debouncing
     */
    onSearchValueChange(searchValue: string): void {
        this.searchValue = searchValue;
        this.searchLoading.set(true);

        // Clear existing debounce timer
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }

        // If search is empty, reset to show all threads
        if (!searchValue?.trim()) {
            this.updateSearchCommandItems();
            this.searchLoading.set(false);
            this.queryClient.cancelQueries({ queryKey: threadKeys.search });
            return;
        }

        // Debounce search API call
        this.searchDebounceTimer = setTimeout(() => {
            if (this.searchValue === searchValue && searchValue.trim()) {
                this.queryClient.resetQueries({ queryKey: threadKeys.search });
                this.searchThreadsQuery.refetch().then(() => {
                    this.searchLoading.set(false);
                });
            }
        }, 300);
    }

    /**
     * Converts search results to command items for display
     */
    updateSearchCommandItems(): void {
        const results = this.searchValue.trim() ? this.searchResults() : this.threads();
        this.searchCommandItems = results.map(thread => {
            const group = this.getDateGroup(thread.createdAt);
            return {
                id: thread._id,
                label: thread.title || 'Untitled Thread',
                icon: 'message-circle' as const,
                timestamp: this.formatTimestamp(thread.createdAt, group),
                tooltipDate: this.formatTooltipDate(thread.createdAt),
                dateGroup: group,
                ...thread
            };
        });
    }

    getDateGroup(dateString: string): DateGroup {
        if (!dateString) return DateGroup.OLDER;
        const m = moment(dateString);
        if (m.isSame(moment(), 'day')) return DateGroup.TODAY;
        if (m.isAfter(moment().subtract(7, 'days').startOf('day'))) return DateGroup.LAST_7_DAYS;
        return DateGroup.OLDER;
    }

    formatTimestamp(dateString: string, group?: DateGroup): string {
        if (!dateString) return '';
        const m = moment(dateString);
        const resolvedGroup = group ?? this.getDateGroup(dateString);
        if (resolvedGroup === DateGroup.TODAY) return m.format('HH:mm');
        if (resolvedGroup === DateGroup.LAST_7_DAYS) return m.format('ddd');
        return m.format('D MMM');
    }

    formatTooltipDate(dateString: string): string {
        if (!dateString) return '';
        return moment(dateString).format('dddd, D MMMM YYYY [at] HH:mm');
    }

    /**
     * Computed property for search loading state
     */
    get isSearchLoading(): boolean {
        return this.searchLoading() || (this.threadsQuery.isLoading() && this.threads().length === 0);
    }

    /**
     * Sets up scroll listener for infinite scroll in search results
     */
    setupSearchScrollListener(): void {
        this.cleanupSearchScrollListener();

        const commandList = document.getElementById('command-list');
        if (commandList) {
            this.searchScrollListener = this.onSearchScroll.bind(this);
            commandList.addEventListener('scroll', this.searchScrollListener);
        }
    }

    /**
     * Handles infinite scroll in search results
     */
    onSearchScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const threshold = 20;
        const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

        if (atBottom && this.searchValue.trim()) {
            if (this.searchThreadsQuery.hasNextPage() && !this.searchThreadsQuery.isFetchingNextPage()) {
                this.searchThreadsQuery.fetchNextPage();
            }
        }
    }

    /**
     * Handles thread selection from search results
     */
    onThreadSelected(item: CommandItem): void {
        const results = this.searchValue.trim() ? this.searchResults() : this.threads();
        const thread = results.find(t => t._id === item.id);
        if (thread) {
            this.closeSearchDialog();
            this.router.navigate(['/bob/thread'], {
                queryParams: {
                    threadId: thread._id,
                    threadMetadataId: thread.threadMetadataId
                }
            });
        }
    }

    /**
     * Closes search dialog and cleans up
     */
    closeSearchDialog(): void {
        this.cleanupSearchListeners();
        this.searchCommandItems = [];
        this.searchValue = '';
        this.queryClient.resetQueries({ queryKey: threadKeys.search });
        this.searchDialogVisible = false;
    }

    /**
     * Cleans up search-related event listeners
     */
    private cleanupSearchScrollListener(): void {
        if (this.searchScrollListener) {
            const commandList = document.getElementById('command-list');
            if (commandList) {
                commandList.removeEventListener('scroll', this.searchScrollListener);
            }
            this.searchScrollListener = undefined;
        }
    }

    /**
     * Cleans up all search-related resources
     */
    private cleanupSearchListeners(): void {
        this.cleanupSearchScrollListener();

        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = undefined;
        }
    }

    // ============================================
    // Memory Management
    // ============================================
    /**
     * Opens the manage memories dialog
     */
    openManageMemoriesDialog(): void {
        this.showManageMemoriesDialog.set(true);
    }

    /**
     * Opens the manage skills dialog
     */
    openManageSkillsDialog(): void {
        this.skillsDialogVisible.set(true);
    }

    // ============================================
    // Prompt Library Walkthrough
    // ============================================
    private checkPromptLibraryWalkthrough(): void {
        this.authService.currentUser.pipe(take(1)).subscribe(user => {
            if (!user) return;
            const seen = user.productWalkThrough?.find(
                w => w.type === PROMPT_LIBRARY_WALKTHROUGH_KEY
            );
            if (!seen?.status) {
                const el = document.getElementById(SIDEBAR_IDS.MAIN);
                if (el) {
                    this.promptLibraryHighlighted.set(true);
                    this.walkthroughRef.show(el);
                }
            }
        });
    }

    private markPromptLibraryWalkthroughSeen(): void {
        this.authService.currentUser.pipe(take(1)).subscribe(user => {
            const seen = user?.productWalkThrough?.find(w => w.type === PROMPT_LIBRARY_WALKTHROUGH_KEY);
            if (!seen?.status) {
                this.userService.updateWalkThrough({ type: PROMPT_LIBRARY_WALKTHROUGH_KEY, status: true }).subscribe();
            }
        });
    }

    onPromptLibraryWalkthroughGotIt(): void {
        this.promptLibraryHighlighted.set(false);
        this.walkthroughRef.hide();
        this.markPromptLibraryWalkthroughSeen();
    }

    onPromptLibraryWalkthroughDismissed(): void {
        this.promptLibraryHighlighted.set(false);
        this.markPromptLibraryWalkthroughSeen();
    }

    onPromptLibraryWalkthroughCheckPrompts(): void {
        this.promptLibraryHighlighted.set(false);
        this.walkthroughRef.hide();
        this.markPromptLibraryWalkthroughSeen();
        this.router.navigate(['/bob/prompt-library']);
    }
}
